import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { transcribeAudio, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Hold-to-talk mic button that sends captured audio to /api/voice/transcribe.
 *  Calls `onTranscript(text)` with the final text, or `onError(message)` on failure.
 */
export const VoiceInput = ({ onTranscript, onError, disabled, className = "" }) => {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (disabled || state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) {
          setState("idle");
          return;
        }
        setState("processing");
        try {
          const text = await transcribeAudio(blob);
          if (text && text.trim()) {
            onTranscript?.(text.trim());
          } else {
            onError?.("No speech detected");
            toast.error("No speech detected — try again");
          }
        } catch (e) {
          const msg = formatApiError(e.response?.data?.detail) || e.message;
          onError?.(msg);
          toast.error(msg);
        } finally {
          setState("idle");
        }
      };
      recorderRef.current = rec;
      rec.start();
      setState("recording");
    } catch (e) {
      toast.error("Microphone access denied");
      setState("idle");
    }
  };

  const stop = () => {
    const rec = recorderRef.current;
    if (rec && rec.state === "recording") rec.stop();
  };

  const busy = state === "processing";
  const recording = state === "recording";

  return (
    <button
      type="button"
      data-testid="voice-input-btn"
      aria-label={recording ? "Stop recording" : "Start voice input"}
      aria-pressed={recording}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={() => recording && stop()}
      onTouchStart={(e) => { e.preventDefault(); start(); }}
      onTouchEnd={(e) => { e.preventDefault(); stop(); }}
      disabled={disabled || busy}
      className={`inline-flex items-center justify-center h-11 w-11 rounded-full border transition-colors ${
        recording
          ? "bg-rose-500 border-rose-400 text-white animate-pulse"
          : busy
          ? "bg-white/5 border-white/10 text-slate-500"
          : "bg-[#07080A] border-white/10 text-slate-300 hover:border-amber-400/60 hover:text-amber-300"
      } ${className}`}
      title={recording ? "Release to send" : busy ? "Transcribing…" : "Hold to speak"}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : recording ? (
        <Square className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </button>
  );
};

export default VoiceInput;
